import React, { useState, useRef, useEffect } from 'react';
import api from '../services/api';
import './AgenticAIChat.css';

/**
 * ONTAP 2.0 Agentic AI Chat
 *
 * Demonstrates multi-agent architecture with visual feedback
 * showing which agents are working on the user's request.
 */
const AgenticAIChat = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeAgents, setActiveAgents] = useState([]);
  const [agentActivity, setAgentActivity] = useState([]);
  const [showAgentPanel, setShowAgentPanel] = useState(false);
  const [isListening, setIsListening] = useState(false);

  const messagesEndRef = useRef(null);
  const recognitionRef = useRef(null);

  const user = JSON.parse(localStorage.getItem('user'));

  // Agent definitions
  const agents = {
    academic: { name: 'Academic', emoji: '📚', color: '#667eea' },
    mobility: { name: 'Mobility', emoji: '🚗', color: '#f59e0b' },
    social: { name: 'Social', emoji: '👥', color: '#10b981' },
    wellness: { name: 'Wellness', emoji: '🧘', color: '#ec4899' }
  };

  // Initialize speech recognition
  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = 'en-US';

      recognitionRef.current.onresult = (event) => {
        const transcript = Array.from(event.results)
          .map(result => result[0].transcript)
          .join('');
        setInput(transcript);
      };

      recognitionRef.current.onend = () => setIsListening(false);
      recognitionRef.current.onerror = () => setIsListening(false);
    }
  }, []);

  // Auto-scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, agentActivity]);

  // Welcome message
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([{
        type: 'system',
        content: `Welcome to ONTAP 2.0! 🎓\n\nI'm your AI-powered campus assistant with specialized agents:\n\n📚 **Academic** - Tutoring, schedules, study help\n🚗 **Mobility** - Rides, airport, navigation\n👥 **Social** - Community, housing, events\n🧘 **Wellness** - Mental health, food, fitness\n\nTry: "I'm stressed about finals and need a ride to the airport"`,
        timestamp: new Date()
      }]);
    }
  }, [isOpen, messages.length]);

  const sendMessage = async (messageText = input) => {
    if (!messageText.trim()) return;

    const userMessage = {
      type: 'user',
      content: messageText,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);
    setActiveAgents([]);
    setAgentActivity([]);

    // Simulate agent activation for demo
    simulateAgentActivation(messageText);

    try {
      // Use agent endpoint if logged in, otherwise simulate
      const endpoint = user ? '/agent/chat' : '/ai/chat';
      const response = await api.post(endpoint, { message: messageText });

      const assistantMessage = {
        type: 'assistant',
        content: response.data.response,
        timestamp: new Date(),
        agents: response.data.agentActivity || [],
        primaryAgent: response.data.primaryAgent,
        contributingAgents: response.data.contributingAgents || [],
        suggestions: response.data.suggestions,
        executionTime: response.data.executionTime
      };

      setMessages(prev => [...prev, assistantMessage]);

      // Store final agent activity
      if (response.data.agentActivity) {
        setAgentActivity(response.data.agentActivity);
      }

    } catch (error) {
      console.error('Agent chat error:', error);

      // Fallback response with simulated agent behavior
      const fallbackMessage = {
        type: 'assistant',
        content: getFallbackResponse(messageText),
        timestamp: new Date(),
        primaryAgent: detectPrimaryAgent(messageText),
        contributingAgents: []
      };

      setMessages(prev => [...prev, fallbackMessage]);
    } finally {
      setLoading(false);
      setTimeout(() => setActiveAgents([]), 500);
    }
  };

  const simulateAgentActivation = (message) => {
    const lowerMessage = message.toLowerCase();
    const activations = [];

    // Determine which agents should activate
    if (/tutor|study|class|exam|homework|schedule/i.test(lowerMessage)) {
      activations.push('academic');
    }
    if (/ride|drive|airport|carpool|parking/i.test(lowerMessage)) {
      activations.push('mobility');
    }
    if (/isa|community|housing|roommate|event|whatsapp/i.test(lowerMessage)) {
      activations.push('social');
    }
    if (/stress|food|eat|gym|tired|mental|wellness/i.test(lowerMessage)) {
      activations.push('wellness');
    }

    // Default to social for general queries
    if (activations.length === 0) {
      activations.push('social');
    }

    // Stagger agent activations for visual effect
    activations.forEach((agent, index) => {
      setTimeout(() => {
        setActiveAgents(prev => [...prev, agent]);
        setAgentActivity(prev => [...prev, {
          timestamp: Date.now(),
          type: 'activation',
          message: `${agents[agent].emoji} ${agents[agent].name} Agent activated`
        }]);
      }, index * 300);
    });
  };

  const detectPrimaryAgent = (message) => {
    const lowerMessage = message.toLowerCase();
    if (/tutor|study|class|exam/i.test(lowerMessage)) return 'academic';
    if (/ride|drive|airport/i.test(lowerMessage)) return 'mobility';
    if (/stress|food|gym|tired/i.test(lowerMessage)) return 'wellness';
    return 'social';
  };

  const getFallbackResponse = (message) => {
    const agent = detectPrimaryAgent(message);
    const responses = {
      academic: "📚 I can help with tutoring, schedules, and study resources. Sign in to access personalized academic support!",
      mobility: "🚗 Looking for rides or transportation? Sign in to see available carpools and airport rides!",
      social: "👥 Connect with the campus community! Sign in to access ISA, housing, events, and more.",
      wellness: "🧘 Your wellness matters! I can help with stress management, food options, and fitness resources."
    };
    return responses[agent];
  };

  const handleVoiceInput = () => {
    if (!recognitionRef.current) {
      alert('Voice input not supported. Try Chrome or Edge.');
      return;
    }

    if (isListening) {
      recognitionRef.current.stop();
    } else {
      recognitionRef.current.start();
      setIsListening(true);
    }
  };

  const handleQuickAction = (action) => {
    const actions = {
      'plan_day': 'Help me plan my day today',
      'airport': 'I need a ride to the airport',
      'stress': "I'm feeling stressed about school",
      'isa': 'Show me the ISA community',
      'tutor': 'I need a math tutor',
      'housing': "I'm looking for housing near campus"
    };
    sendMessage(actions[action] || action);
  };

  const formatMessage = (content) => {
    return content
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\n/g, '<br/>');
  };

  return (
    <>
      {/* Floating Button */}
      <button
        className={`agentic-ai-button ${isOpen ? 'open' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        {isOpen ? (
          <span className="close-icon">×</span>
        ) : (
          <>
            <span className="ai-icon">🤖</span>
            <span className="ai-badge">2.0</span>
            <span className="ai-pulse"></span>
          </>
        )}
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div className="agentic-ai-chat">
          {/* Header */}
          <div className="chat-header">
            <div className="header-left">
              <span className="header-logo">🎓</span>
              <div className="header-text">
                <h3>ONTAP 2.0</h3>
                <span className="header-subtitle">Multi-Agent Campus AI</span>
              </div>
            </div>
            <div className="header-right">
              <button
                className={`agent-toggle ${showAgentPanel ? 'active' : ''}`}
                onClick={() => setShowAgentPanel(!showAgentPanel)}
                title="Show agent activity"
              >
                🔬
              </button>
              <button className="close-btn" onClick={() => setIsOpen(false)}>×</button>
            </div>
          </div>

          {/* Agent Status Bar */}
          <div className="agent-status-bar">
            {Object.entries(agents).map(([key, agent]) => (
              <div
                key={key}
                className={`agent-indicator ${activeAgents.includes(key) ? 'active' : ''}`}
                style={{ '--agent-color': agent.color }}
              >
                <span className="agent-emoji">{agent.emoji}</span>
                <span className="agent-name">{agent.name}</span>
                {activeAgents.includes(key) && (
                  <span className="agent-working">
                    <span className="dot"></span>
                    <span className="dot"></span>
                    <span className="dot"></span>
                  </span>
                )}
              </div>
            ))}
          </div>

          <div className="chat-body">
            {/* Messages Panel */}
            <div className={`messages-panel ${showAgentPanel ? 'with-sidebar' : ''}`}>
              <div className="chat-messages">
                {messages.map((msg, index) => (
                  <div key={index} className={`message ${msg.type}`}>
                    {msg.type === 'assistant' && (
                      <div className="message-avatar">
                        {msg.primaryAgent ? agents[msg.primaryAgent]?.emoji : '🤖'}
                      </div>
                    )}
                    {msg.type === 'system' && (
                      <div className="message-avatar system">🎓</div>
                    )}
                    <div className="message-content">
                      <div
                        className="message-text"
                        dangerouslySetInnerHTML={{ __html: formatMessage(msg.content) }}
                      />
                      {msg.type === 'assistant' && msg.primaryAgent && (
                        <div className="message-meta">
                          <span
                            className="agent-badge"
                            style={{ '--agent-color': agents[msg.primaryAgent]?.color }}
                          >
                            {agents[msg.primaryAgent]?.name}
                          </span>
                          {msg.contributingAgents?.map((a, i) => (
                            <span
                              key={i}
                              className="agent-badge contrib"
                              style={{ '--agent-color': agents[a]?.color }}
                            >
                              +{agents[a]?.name}
                            </span>
                          ))}
                          {msg.executionTime && (
                            <span className="execution-time">{msg.executionTime}ms</span>
                          )}
                        </div>
                      )}
                      {msg.suggestions && msg.suggestions.length > 0 && (
                        <div className="message-suggestions">
                          {msg.suggestions.slice(0, 3).map((s, i) => (
                            <button
                              key={i}
                              className="suggestion-btn"
                              onClick={() => sendMessage(s.text || s)}
                            >
                              {s.text || s}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    {msg.type === 'user' && (
                      <div className="message-avatar user">👤</div>
                    )}
                  </div>
                ))}

                {loading && (
                  <div className="message assistant">
                    <div className="message-avatar">🤖</div>
                    <div className="message-content">
                      <div className="thinking-indicator">
                        <span>Agents collaborating</span>
                        <div className="thinking-dots">
                          <span></span><span></span><span></span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>
            </div>

            {/* Agent Activity Panel */}
            {showAgentPanel && (
              <div className="agent-panel">
                <h4>Agent Activity</h4>
                <div className="activity-log">
                  {agentActivity.length > 0 ? (
                    agentActivity.map((activity, i) => (
                      <div key={i} className="activity-item">
                        <span className="activity-time">
                          {new Date(activity.timestamp).toLocaleTimeString([], {
                            hour: '2-digit',
                            minute: '2-digit',
                            second: '2-digit'
                          })}
                        </span>
                        <span className="activity-message">{activity.message}</span>
                      </div>
                    ))
                  ) : (
                    <p className="no-activity">No recent activity</p>
                  )}
                </div>
                <div className="agent-legend">
                  <h5>Agents</h5>
                  {Object.entries(agents).map(([key, agent]) => (
                    <div key={key} className="legend-item">
                      <span className="legend-emoji">{agent.emoji}</span>
                      <span className="legend-name">{agent.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Quick Actions */}
          <div className="quick-actions-bar">
            <button onClick={() => handleQuickAction('plan_day')}>📅 Plan Day</button>
            <button onClick={() => handleQuickAction('airport')}>✈️ Airport</button>
            <button onClick={() => handleQuickAction('isa')}>🇮🇳 ISA</button>
            <button onClick={() => handleQuickAction('stress')}>🧘 Stress</button>
            <button onClick={() => handleQuickAction('tutor')}>📚 Tutor</button>
          </div>

          {/* Input Area */}
          <div className="chat-input-area">
            <div className="input-row">
              <button
                className={`voice-btn ${isListening ? 'listening' : ''}`}
                onClick={handleVoiceInput}
              >
                {isListening ? '🔴' : '🎤'}
              </button>
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                placeholder={isListening ? 'Listening...' : 'Ask anything about campus life...'}
                disabled={loading}
              />
              <button
                className="send-btn"
                onClick={() => sendMessage()}
                disabled={loading || !input.trim()}
              >
                {loading ? '...' : '➤'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AgenticAIChat;
